import { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import BackMethods from "../../back_component/BackMethods";

function ModalHeaderPro({ title, onHide }) {
  return (
    <div className="users-admin-modal__header">
      <div className="users-admin-modal__header-main">
        <h5 className="users-admin-modal__title mb-0">{title}</h5>
      </div>
      <button type="button" className="btn-close" onClick={onHide} aria-label="إغلاق" />
    </div>
  );
}

function InlineField({ label, htmlFor, children }) {
  return (
    <div className="users-admin-modal__inline-field">
      <Form.Label htmlFor={htmlFor} className="users-admin-modal__inline-label">
        {label}
      </Form.Label>
      <div className="users-admin-modal__inline-control">{children}</div>
    </div>
  );
}

function CreateFacilityTypeModal({ show, onHide, onSaved }) {
  const { backEnd_request } = BackMethods();
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!show) return;
    setName("");
    setFormError("");
    setIsSubmitting(false);
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!name.trim()) {
      setFormError("يرجى إدخال اسم النوع.");
      return;
    }
    setIsSubmitting(true);
    try {
      await backEnd_request.post("/FacilityTypes", { name: name.trim() });
      onSaved?.({ message: "تم إضافة نوع الفعالية بنجاح ✅", variant: "success" });
      onHide();
    } catch (err) {
      setFormError(err.response?.data?.message || "حدث خطأ أثناء الإضافة");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="users-admin-modal-dialog"
      className="users-admin-modal users-admin-modal--compact"
      dir="rtl"
      backdrop="static"
    >
      <ModalHeaderPro title="إضافة نوع فعالية" onHide={onHide} />
      <Modal.Body>
        <p className="users-admin-modal__hint">
          <i className="bi bi-plus-circle" />
          <span>أدخل اسم النوع كما سيظهر في تصنيف المنشآت والتقارير.</span>
        </p>
        <Form onSubmit={handleSubmit}>
          <Row className="g-2 gx-lg-3">
            <Col lg={12}>
              <InlineField label="اسم النوع" htmlFor="facility-type-name">
                <Form.Control
                  id="facility-type-name"
                  size="sm"
                  className="rounded-2"
                  placeholder="أدخل اسم النوع..."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </InlineField>
            </Col>
          </Row>
          {formError ? (
            <div className="alert alert-danger py-1 px-2 small mb-0 mt-2" role="alert">
              {formError}
            </div>
          ) : null}
          <div className="users-admin-modal__footer d-flex gap-2 justify-content-end mt-3">
            <Button variant="outline-secondary" size="sm" type="button" onClick={onHide} disabled={isSubmitting}>
              إلغاء
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="ms-1" /> إضافة
                </>
              ) : (
                "إضافة النوع"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

function EditFacilityTypeModal({ show, facilityTypeId, onHide, onSaved }) {
  const { backEnd_request } = BackMethods();
  const [name, setName] = useState("");
  const [initialName, setInitialName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!show || !facilityTypeId) return;
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        setFormError("");
        const res = await backEnd_request.get(`/FacilityTypes/${facilityTypeId}`);
        if (cancelled) return;
        const data = res.data || res || {};
        const n = data.name ?? "";
        setName(n);
        setInitialName(n);
      } catch (err) {
        if (!cancelled) {
          setFormError(err.response?.data?.message || "فشل جلب بيانات النوع");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, facilityTypeId, backEnd_request]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!name.trim()) {
      setFormError("يرجى إدخال اسم النوع.");
      return;
    }
    setIsSubmitting(true);
    try {
      await backEnd_request.post(`/FacilityTypes/${facilityTypeId}`, { name: name.trim() });
      onSaved?.({ message: "تم تعديل نوع الفعالية بنجاح ✅", variant: "success" });
      onHide();
    } catch (err) {
      setFormError(err.response?.data?.message || "تعذر حفظ التعديلات");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="users-admin-modal-dialog"
      className="users-admin-modal users-admin-modal--compact"
      dir="rtl"
      backdrop="static"
    >
      <ModalHeaderPro title="تعديل نوع فعالية" onHide={onHide} />
      <Modal.Body>
        {initialName ? (
          <div className="users-admin-modal__user-chip mb-2 py-1">
            <i className="bi bi-building text-secondary small" />
            <span className="text-muted" style={{ fontSize: "0.75rem" }}>
              النوع الحالي
            </span>
            <strong className="small">{initialName}</strong>
          </div>
        ) : null}
        <Form onSubmit={handleSubmit}>
          {isLoading ? (
            <div className="text-center py-2">
              <Spinner animation="border" size="sm" />
            </div>
          ) : null}
          <Row className="g-2 gx-lg-3">
            <Col lg={12}>
              <InlineField label="اسم النوع" htmlFor="facility-type-edit-name">
                <Form.Control
                  id="facility-type-edit-name"
                  size="sm"
                  className="rounded-2"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isLoading}
                />
              </InlineField>
            </Col>
          </Row>
          {formError ? (
            <div className="alert alert-danger py-1 px-2 small mb-0 mt-2" role="alert">
              {formError}
            </div>
          ) : null}
          <div className="users-admin-modal__footer d-flex gap-2 justify-content-end mt-3">
            <Button variant="outline-secondary" size="sm" type="button" onClick={onHide} disabled={isSubmitting}>
              إلغاء
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={isSubmitting || isLoading}>
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="ms-1" /> حفظ
                </>
              ) : (
                "حفظ التغييرات"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default function FacilityTypeManagementModals({
  modal,
  facilityTypeId,
  onClose,
  onSaved,
  canCreate = true,
  canEdit = true,
}) {
  return (
    <>
      <CreateFacilityTypeModal show={canCreate && modal === "create"} onHide={onClose} onSaved={onSaved} />
      <EditFacilityTypeModal
        show={canEdit && modal === "edit"}
        facilityTypeId={facilityTypeId}
        onHide={onClose}
        onSaved={onSaved}
      />
    </>
  );
}
